/**
 * Registro de TODO lo que llama a Bedrock (08/10/2026).
 *
 * Por qué existe: DevOps reportó $582 de Bedrock en septiembre y ~$55/día en
 * octubre, y el tab "Bedrock AI" de /ia-native/observability no podía decir de
 * qué era: el union de `feature` vivía inline en bedrock-logger.ts, el front
 * tenía un FEATURE_META con 4 entradas (todo lo demás salía como "—"), había
 * llamadas sin loguear (resúmenes de archivos de Slack, grounding-check,
 * report-sections) y el precio por modelo estaba solo en el logger.
 *
 * Acá vive, UNA vez, para backend y frontend:
 *  - `BEDROCK_FEATURES`: cada feature → módulo del BO + etiqueta + tipo de
 *    tráfico. El tipo `BedrockFeature` (lo que acepta `logBedrockCall`) se
 *    deriva de las claves: una feature nueva se agrega acá y el logger, el
 *    dashboard y los cortes por módulo la toman solos.
 *  - `BEDROCK_MODELS`: id del modelo → etiqueta + tarifa (USD por 1M tokens).
 *    `calcBedrockCost` es la misma cuenta para una fila del log y para los
 *    tokens que reporta CloudWatch.
 *
 * Quién es "QA" no se decide acá: lo dice `actor_kind` de la fila (bots de
 * shared/service-accounts.ts). `traffic` solo distingue lo que dispara una
 * persona de lo que corre solo (syncs, crons) y de lo que existe para medir
 * calidad (jueces de evals).
 */

export type BedrockModule =
  | 'brain'
  | 'ta'
  | 'people'
  | 'performance'
  | 'partnerships'
  | 'oml'
  | 'supportia'
  | 'vault'
  | 'platform';

/** product: lo dispara una persona · sync: corre solo (cron/conector) · qa: juez de evals. */
export type BedrockTraffic = 'product' | 'sync' | 'qa';

export interface BedrockFeatureMeta {
  /** Etiqueta corta para el dashboard (nombre técnico, igual en los 3 idiomas). */
  label: string;
  module: BedrockModule;
  traffic: BedrockTraffic;
}

// i18n-ok: etiquetas técnicas de features, iguales en los 3 idiomas (el módulo sí se traduce)
export const BEDROCK_FEATURES = {
  // ── Winclap Brain ────────────────────────────────────────────────────────
  brain_retrieval:         { label: 'Síntesis de respuesta',        module: 'brain', traffic: 'product' },
  brain_reranking:         { label: 'Reranking',                    module: 'brain', traffic: 'product' },
  brain_grounding_check:   { label: 'Verificación de grounding',    module: 'brain', traffic: 'product' },
  brain_report_sections:   { label: 'Filtro de secciones (reporte)', module: 'brain', traffic: 'product' },
  brain_admin_chat:        { label: 'Asistente de Brain (Salud)',   module: 'brain', traffic: 'product' },
  brain_area_people:       { label: 'Área People',                  module: 'brain', traffic: 'product' },
  brain_area_finance:      { label: 'Área Finance',                 module: 'brain', traffic: 'product' },
  brain_area_pipeline:     { label: 'Área Pipeline comercial',      module: 'brain', traffic: 'product' },
  brain_weekly_highlights: { label: 'Highlights semanales',         module: 'brain', traffic: 'sync' },
  brain_sentiment:         { label: 'Sentimiento de cliente',       module: 'brain', traffic: 'sync' },
  brain_meet_attribution:  { label: 'Atribución de Meet',           module: 'brain', traffic: 'sync' },
  brain_meet_summary:      { label: 'Resumen de Meet',              module: 'brain', traffic: 'sync' },
  brain_slack_file_summary:{ label: 'Resumen de archivos de Slack', module: 'brain', traffic: 'sync' },
  brain_eval_judge:        { label: 'Juez de evals',                module: 'brain', traffic: 'qa' },
  // ── Talent Acquisition ───────────────────────────────────────────────────
  ta_metrics_agent:        { label: 'Agente TA Metrics',            module: 'ta', traffic: 'product' },
  ta_intake_agent:         { label: 'Hiring Manager Intake',        module: 'ta', traffic: 'product' },
  ta_interview_agent:      { label: 'Interview Plan',               module: 'ta', traffic: 'product' },
  // ── People ───────────────────────────────────────────────────────────────
  people_landing_translate:{ label: 'Traducción de la landing',     module: 'people', traffic: 'product' },
  knowledge_base:          { label: 'Base de conocimiento',         module: 'people', traffic: 'product' },
  notification_templates_ai:{ label: 'Plantillas de notificación',  module: 'people', traffic: 'product' },
  // ── Performance (Flavia, D-Talk, Mapa de Talento) ────────────────────────
  flavia_chat:             { label: 'Flavia',                       module: 'performance', traffic: 'product' },
  result_analysis:         { label: 'Análisis de resultados',       module: 'performance', traffic: 'product' },
  talent_map_chat:         { label: 'Chat del Mapa de Talento',     module: 'performance', traffic: 'product' },
  dtalk_chat:              { label: 'D-Talk',                       module: 'performance', traffic: 'product' },
  dtalk_translate:         { label: 'Traducción de D-Talk',         module: 'performance', traffic: 'product' },
  // ── Partnerships / Owned Media / supportIA / Vault ───────────────────────
  partnerships_assistant:  { label: 'Asistente de Partnerships',    module: 'partnerships', traffic: 'product' },
  oml_assistant:           { label: 'supportIA sobre Owned Media',  module: 'oml', traffic: 'product' },
  /** Org Service (T3b): el manager describe por chat quién trabaja con qué cliente; el agente deja sugeridas, nunca confirma. */
  org_assignment_agent:    { label: 'Agente de asignaciones (Org)',  module: 'platform', traffic: 'product' },
  /** Brain dentro del Product Tour del Studio (Learning): pista y dudas por paso, Haiku. */
  studio_tour_brain:       { label: 'Brain del Product Tour',      module: 'people', traffic: 'product' },
  supportia_chat:          { label: 'supportIA',                    module: 'supportia', traffic: 'product' },
  vault_mfa_assistant:     { label: 'Asistente de MFA del Vault',   module: 'vault', traffic: 'product' },
} as const satisfies Record<string, BedrockFeatureMeta>;

export type BedrockFeature = keyof typeof BEDROCK_FEATURES;

export const BEDROCK_MODULES: readonly BedrockModule[] = [
  'brain', 'ta', 'people', 'performance', 'partnerships', 'oml', 'supportia', 'vault', 'platform',
];

/** Meta de una feature; una desconocida (fila vieja, typo) cae en `platform` sin romper el dashboard. */
export function bedrockFeatureMeta(feature: string | null | undefined): BedrockFeatureMeta {
  const meta = (BEDROCK_FEATURES as Record<string, BedrockFeatureMeta>)[feature ?? ''];
  return meta ?? { label: feature || '—', module: 'platform', traffic: 'product' };
}

// ── Modelos y tarifas ──────────────────────────────────────────────────────

export interface BedrockModelMeta {
  label: string;
  /** USD por 1M tokens (cross-region, us-east-1). */
  input: number;
  output: number;
}

/**
 * Ids tal como se invocan (perfiles cross-region `us.`). CloudWatch los reporta
 * con el mismo id, a veces sin el prefijo `us.` o como ARN: `bedrockModelMeta`
 * matchea por familia para no perder costo por una variante del nombre.
 */
export const BEDROCK_MODELS: Record<string, BedrockModelMeta> = {
  'us.anthropic.claude-haiku-4-5-20251001-v1:0': { label: 'Haiku 4.5', input: 0.80, output: 4.00 },
  'us.anthropic.claude-sonnet-4-6':              { label: 'Sonnet 4.6', input: 3.00, output: 15.00 },
};

/** Tarifa si el id no está en la tabla y no se reconoce la familia (nunca cero: subreportar es peor). */
const UNKNOWN_MODEL: BedrockModelMeta = { label: 'otro modelo', input: 1.0, output: 5.0 };
const EMBEDDINGS: BedrockModelMeta = { label: 'Titan Embeddings', input: 0.02, output: 0 };

/** Titan (embeddings): se cobra por llamada en CloudWatch pero no se loguea por llamada (10-25k/día, ~$0,2). */
export function isEmbeddingModel(modelId: string | null | undefined): boolean {
  return bedrockModelMeta(modelId) === EMBEDDINGS;
}

export function bedrockModelMeta(modelId: string | null | undefined): BedrockModelMeta {
  const id = modelId ?? '';
  if (BEDROCK_MODELS[id]) return BEDROCK_MODELS[id];
  const exact = Object.entries(BEDROCK_MODELS).find(([k]) => id.endsWith(k) || id.endsWith(k.replace(/^us\./, '')));
  if (exact) return exact[1];
  const low = id.toLowerCase();
  if (low.includes('titan-embed')) return EMBEDDINGS;
  if (low.includes('haiku')) return { ...UNKNOWN_MODEL, label: 'Haiku (otra versión)', input: 0.80, output: 4.00 };
  if (low.includes('sonnet')) return { ...UNKNOWN_MODEL, label: 'Sonnet (otra versión)', input: 3.00, output: 15.00 };
  if (low.includes('opus')) return { ...UNKNOWN_MODEL, label: 'Opus', input: 15.00, output: 75.00 };
  return UNKNOWN_MODEL;
}

/**
 * Costo de una llamada. Con prompt caching el `input_tokens` de la API ya
 * EXCLUYE los tokens cacheados: las lecturas de caché se cobran al 10 % del
 * input y las escrituras (TTL 5 min) al 125 %.
 */
export function calcBedrockCost(
  modelId: string | null | undefined,
  inputTokens: number,
  outputTokens: number,
  cacheReadTokens = 0,
  cacheWriteTokens = 0,
): number {
  const p = bedrockModelMeta(modelId);
  return (
    inputTokens * p.input +
    cacheReadTokens * p.input * 0.1 +
    cacheWriteTokens * p.input * 1.25 +
    outputTokens * p.output
  ) / 1_000_000;
}

// ── Ventana del tab Bedrock ────────────────────────────────────────────────

/** Períodos que ofrece el tab (y que acepta el router: el número se interpola en el SQL). */
export const BEDROCK_WINDOWS = [7, 30, 90] as const;
export type BedrockWindowDays = (typeof BEDROCK_WINDOWS)[number];
export const isBedrockWindow = (v: unknown): v is BedrockWindowDays =>
  typeof v === 'number' && (BEDROCK_WINDOWS as readonly number[]).includes(v);

// ── Bloques del dashboard ──────────────────────────────────────────────────

/**
 * Cómo agrupa el tab Bedrock cada fila del log: personas (Brain / resto del
 * Back Office), lo que corre solo (syncs, crons) y lo sintético (bots de
 * staging/smoke/eval, jueces). `actorKind` es el de la fila: 'human',
 * 'system' (sin usuario) o un bot de shared/service-accounts.ts.
 */
export type BedrockBlock = 'people_brain' | 'people_bo' | 'sync' | 'qa';

export const BEDROCK_BLOCKS: readonly BedrockBlock[] = ['people_brain', 'people_bo', 'sync', 'qa'];

export function bedrockBlockOf(feature: string | null | undefined, actorKind: string | null | undefined): BedrockBlock {
  const meta = bedrockFeatureMeta(feature);
  const actor = actorKind ?? 'human';
  if (meta.traffic === 'qa' || (actor !== 'human' && actor !== 'system')) return 'qa';
  if (meta.traffic === 'sync' || actor === 'system') return 'sync';
  return meta.module === 'brain' ? 'people_brain' : 'people_bo';
}

// ── Agregación para el árbol de gasto ──────────────────────────────────────

/** Una fila del log agregada (lo que devuelve observability.bedrockDaily). */
export interface BedrockCostRow {
  feature: string;
  actor_kind?: string | null;
  cost_usd: number | string | null;
  calls?: number | string | null;
}

export interface BedrockFeatureTotal {
  feature: string;
  module: BedrockModule;
  block: BedrockBlock;
  usd: number;
  calls: number;
}

export interface BedrockCostSummary {
  total: number;
  byBlock: Record<BedrockBlock, number>;
  /** Winclap Brain: personas + indexación automática + pruebas/bots sobre el Brain. */
  brain: { people: number; sync: number; qa: number; total: number };
  /** Back Office (todo lo que no es Brain): personas por apartado + pruebas/bots sobre esos agentes. */
  bo: { people: number; qa: number; total: number };
  /** Por apartado del BO (sin el Brain), solo lo que disparó una persona. */
  byModulePeople: Partial<Record<BedrockModule, number>>;
  /** Por apartado del BO, lo sintético (bots / jueces). */
  byModuleQa: Partial<Record<BedrockModule, number>>;
  /** feature + bloque → total (una feature puede estar en dos bloques: personas y bots). */
  features: BedrockFeatureTotal[];
}

const num = (v: unknown): number => Number(v ?? 0) || 0;

/**
 * Suma las filas del log en los bloques del árbol de gasto del tab Bedrock:
 * Brain (personas / sync / QA) y Back Office por apartado (personas / QA). Pura:
 * la misma cuenta en el front y en los tests.
 */
export function summarizeBedrockRows(rows: readonly BedrockCostRow[]): BedrockCostSummary {
  const out: BedrockCostSummary = {
    total: 0,
    byBlock: { people_brain: 0, people_bo: 0, sync: 0, qa: 0 },
    brain: { people: 0, sync: 0, qa: 0, total: 0 },
    bo: { people: 0, qa: 0, total: 0 },
    byModulePeople: {},
    byModuleQa: {},
    features: [],
  };
  const byKey = new Map<string, BedrockFeatureTotal>();
  for (const r of rows) {
    const usd = num(r.cost_usd);
    const block = bedrockBlockOf(r.feature, r.actor_kind);
    const module = bedrockFeatureMeta(r.feature).module;
    out.total += usd;
    out.byBlock[block] += usd;
    // sync es siempre del Brain (las fuentes que se indexan); qa se parte por módulo.
    if (block === 'people_brain') out.brain.people += usd;
    else if (block === 'sync') out.brain.sync += usd;
    else if (block === 'people_bo') out.bo.people += usd;
    else if (module === 'brain') out.brain.qa += usd;
    else out.bo.qa += usd;
    if (module !== 'brain') {
      if (block === 'people_bo') out.byModulePeople[module] = (out.byModulePeople[module] ?? 0) + usd;
      else if (block === 'qa') out.byModuleQa[module] = (out.byModuleQa[module] ?? 0) + usd;
    }
    const k = `${r.feature}|${block}`;
    let f = byKey.get(k);
    if (!f) { f = { feature: r.feature, module, block, usd: 0, calls: 0 }; byKey.set(k, f); }
    f.usd += usd;
    f.calls += num(r.calls);
  }
  out.features = [...byKey.values()].sort((a, b) => b.usd - a.usd);
  out.brain.total = out.brain.people + out.brain.sync + out.brain.qa;
  out.bo.total = out.bo.people + out.bo.qa;
  return out;
}
